import React, { useMemo, useRef, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { ArrowIosDownFill, InfoOutline } from '@lyearn/icons';
import { Loader, Popover, Stack, Tabs, Tooltip, Typography } from '@lyearn/ui';
import Search from '@/modules/corporate/modules/admin/components/Header/Search';
import { SearchBarRef } from '@/modules/corporate/modules/admin/components/Header/types';
import { getFileIcon, getProjectIcon } from '@/modules/tasks/utils/getFileIcon';
import { useFileQuery } from '@/modules/tasks/views/TasksDashboard/graphql/queries/generated/file';

import ProjectsList from './ProjectsList';
import StacksList from './StacksList';

interface TaskLocationSelectorProps {
  value: string | null;
  onChange: (value: string | null) => void;
}

enum TaskLocationTabs {
  PROJECTS = 'projects',
  STACKS = 'stacks',
}

const ICON_SIZE = 22;
const ICON_CLASSNAME = 'text-icon-primary';

function TaskLocationSelector({ value, onChange }: TaskLocationSelectorProps) {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState<TaskLocationTabs>(TaskLocationTabs.PROJECTS);
  const [search, setSearch] = useState<string>('');
  const searchRef = useRef<SearchBarRef>(null);

  // fetch info of selected file
  const [{ data: selectedFile, fetching: fetchingSelectedFile }] = useFileQuery({
    variables: {
      filters: {
        id: value || '',
      },
    },
    pause: !value,
  });

  const renderSelectedOption = () => {
    if (fetchingSelectedFile) {
      return <Loader className="ms-4" />;
    }

    if (value === null) {
      return (
        <Stack alignItems="center" className="ms-4" direction="row" gapX={6}>
          <Stack className="flex-shrink-0">{getProjectIcon(ICON_SIZE, ICON_CLASSNAME)}</Stack>
          <Typography variant="body-short-01">{t('Task:DuplicateTask.Uncategorized')}</Typography>
        </Stack>
      );
    }

    const selectedFileData = selectedFile?.file;
    if (selectedFileData) {
      return (
        <Stack alignItems="center" className="ms-4" direction="row" gapX={6}>
          <Stack className="flex-shrink-0">
            {getFileIcon(selectedFileData, ICON_SIZE, ICON_CLASSNAME)}
          </Stack>
          <Typography variant="body-short-01">{selectedFileData.name}</Typography>
        </Stack>
      );
    }

    return null;
  };

  const tabOptions = useMemo(
    () => [
      {
        label: t('Task:DuplicateTask.Projects'),
        value: TaskLocationTabs.PROJECTS,
      },
      {
        label: t('Task:DuplicateTask.Stacks'),
        value: TaskLocationTabs.STACKS,
      },
    ],
    [t],
  );

  return (
    <Stack className="w-full" direction="col" gapY={8}>
      <Stack alignItems="center" className="w-full ms-8" direction="row" gapX={4}>
        <Typography variant="body-short-02">{t('Task:DuplicateTask.NewTaskLocation')}</Typography>
        <Typography color="text-text-placeholder" variant="body-short-02">
          {t('Task:Optional')}
        </Typography>
        <Tooltip placement="right" title={t('Task:DuplicateTask.TaskLocationHelpText')}>
          <Stack className="flex-shrink-0">
            <InfoOutline fillColorToken="icon-secondary" height={16} width={16} />
          </Stack>
        </Tooltip>
      </Stack>
      <Popover>
        <Popover.Button>
          <Stack
            alignItems="center"
            className="w-full p-12 rounded-8 cursor-pointer border border-border hover:border-border-dark"
            direction="row"
            justifyContent="between">
            {renderSelectedOption()}
            <Stack className="flex-shrink-0">
              <ArrowIosDownFill fillColorToken="icon-secondary" height={24} width={24} />
            </Stack>
          </Stack>
        </Popover.Button>
        <Popover.Content
          className={`!w-[56rem] max-h-[46rem] overflow-y-auto`}
          position="bottom"
          renderAs="popover">
          <Stack className="w-full p-12" direction="col" gapY={24}>
            <Tabs
              className="ms-8"
              tabs={tabOptions}
              value={selectedTab}
              onChange={setSelectedTab}
            />
            <Search
              ref={searchRef}
              className="!p-0 !m-0"
              placeholder={t('Common:Search')}
              searchText={search}
              setSearch={setSearch}
            />
            {selectedTab === TaskLocationTabs.PROJECTS ? (
              <ProjectsList
                search={search}
                selectItem={onChange}
                selectedFileId={value}
                setSearch={setSearch}
              />
            ) : (
              <StacksList
                search={search}
                selectItem={onChange}
                selectedFileId={value}
                setSearch={setSearch}
              />
            )}
          </Stack>
        </Popover.Content>
      </Popover>
    </Stack>
  );
}

export default TaskLocationSelector;
